import React, { createContext, useContext, useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import { toast } from 'sonner';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [otpSession, setOtpSession] = useState(null); // For OTP verification flow
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Check if demo mode is enabled via environment variable
  const isDemoModeEnabled = process.env.REACT_APP_DEMO_MODE === "true";

  // Configure axios default header
  useEffect(() => {
    if (token) {
      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common["Authorization"];
    }
  }, [token]);

  // Check if in demo mode (either via environment variable or demo token)
  const isDemoMode =
    (isDemoModeEnabled && token) || (token && token.startsWith("demo_"));

  // Fetch user profile
  const { data: user, isPending: loading } = useQuery({
    queryKey: ["profile"],
    queryFn: () => {
      if (isDemoMode) {
        // Demo mode - return mock user data
        const demoUser = JSON.parse(localStorage.getItem("demoUser") || "{}");
        console.log("Demo mode - Retrieved user from localStorage:", demoUser);
        if (!demoUser.role || !demoUser.email) {
          console.warn(
            "Demo mode - Invalid user data in localStorage, falling back to API"
          );
          // If localStorage doesn't have valid user data, call API
          return api.get("/api/auth/profile").then((res) => {
            console.log("Demo mode - API response:", res.data);
            const user = res.data.user || res.data.data?.user;
            // Save to localStorage for future use
            localStorage.setItem("demoUser", JSON.stringify(user));
            return user;
          });
        }
        return Promise.resolve(demoUser);
      } else {
        // Real mode - call actual API, handle new response structure
        return api.get("/api/auth/profile").then((res) => {
          const data = res.data;
          return data.user || data.data?.user;
        });
      }
    },
    enabled: !!token, // Only execute query when token exists
    retry: false, // Don't retry to avoid infinite loading
    staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
    gcTime: 1000 * 60 * 10, // Clear cache after 10 minutes
    throwOnError: (error) => {
      console.log("Profile query error:", error);
      console.log("isDemoMode:", isDemoMode);
      console.log("Error status:", error.response?.status);

      // Handle 401 and 403 errors (invalid or expired token)
      // These errors should not occur in demo mode
      if (
        !isDemoMode &&
        (error.response?.status === 401 || error.response?.status === 403)
      ) {
        console.log("Clearing authentication due to 401/403 error");
        setTimeout(() => {
          setToken(null);
          localStorage.removeItem("token");
          localStorage.removeItem("demoUser"); // Clear demo user data
          queryClient.clear();
          delete api.defaults.headers.common["Authorization"];
        }, 0);
      }
      return false; // Don't throw error, let component render normally
    },
  });

  // Ensure loading is false when there is no token
  const actualLoading = token ? loading : false;

  // Login mutation (first step - username/password verification)
  const loginMutation = useMutation({
    mutationFn: (credentials) => {
      if (isDemoModeEnabled) {
        // In demo mode, simulate API response
        return new Promise((resolve) => {
          setTimeout(() => {
            // Check if user wants to skip OTP for any account
            const shouldSkipOtp = credentials.skipOtpForDemo;

            if (shouldSkipOtp) {
              // Skip OTP - return direct login response for any email
              resolve({
                data: {
                  requiresOtp: false,
                  token: `demo_token_${Date.now()}`,
                  user: {
                    email: credentials.email,
                    role: (() => {
                      const email = credentials.email.toLowerCase();
                      console.log("Demo login - Email:", email);

                      let role;
                      if (email.includes("admin")) {
                        role = "super_admin";
                      } else if (
                        email.includes("facility") ||
                        email.includes("fa@")
                      ) {
                        role = "facility_agent";
                      } else if (email.includes('primarysubscriber')) {
                        role = "primary_subscriber";
                      }else if (email.includes("issuer")) {
                        role = "issuer";
                      } else if (email.includes("exchange")) {
                        role = "exchange";
                      } else if (email.includes("arranger")) {
                        role = "arranger";
                      } else if (email.includes("custodian")) {
                        role = "custodian";
                      } else {
                        role = "investor";
                      }

                      console.log("Demo login - Assigned role:", role);
                      return role;
                    })(),
                    id: Date.now(),
                    personalInfo: credentials.email.includes("investor")
                      ? {
                          firstName: "Demo",
                          lastName: "User",
                        }
                      : null,
                    corporateInfo: !credentials.email.includes("investor")
                      ? {
                          companyName: "Demo Company",
                        }
                      : null,
                  },
                },
              });
            } else {
              // Normal demo OTP flow
              resolve({
                data: {
                  requiresOtp: true,
                  sessionId: `demo_session_${Date.now()}`,
                  user: {
                    email: credentials.email,
                    role: (() => {
                      const email = credentials.email.toLowerCase();
                      console.log("Demo login - Email:", email);

                      let role;
                      if (email.includes("admin")) {
                        role = "super_admin";
                      } else if (
                        email.includes("facility") ||
                        email.includes("fa@")
                      ) {
                        role = "facility_agent";  
                      } else if (email.includes('primarysubscriber')) {
                        role = "primary_subscriber";
                      } else if (email.includes("issuer")) {
                        role = "issuer";
                      } else if (email.includes("exchange")) {
                        role = "exchange";
                      } else if (email.includes("arranger")) {
                        role = "arranger";
                      } else if (email.includes("custodian")) {
                        role = "custodian";
                      } else {
                        role = "investor";
                      }

                      console.log("Demo login - Assigned role:", role);
                      return role;
                    })(),
                  },
                },
              });
            }
          }, 1000);
        });
      } else {
        // Real API mode - call the actual login endpoint
        return api.post("/api/auth/login", credentials);
      }
    },
    onSuccess: (response) => {
      if (isDemoModeEnabled || response.data.requiresOtp) {
        // Demo mode or OTP required - set up OTP session
        setOtpSession({
          sessionId: response.data.sessionId,
          user: response.data.user,
          timestamp: Date.now(),
          isDemoAccount: response.data.isDemoAccount || false,
        });
        if (isDemoModeEnabled) {
          toast.success("Demo mode - Enter any 4+ digit OTP");
        } else if (response.data.isDemoAccount) {
          toast.success("Demo mode: Enter 000000 for OTP verification");
        } else {
          toast.success("OTP sent to your email");
        }
      } else {
        // Direct login without OTP (real API mode or demo skip)
        const { token: newToken, user: userData } = response.data;
        setToken(newToken);
        localStorage.setItem("token", newToken);

        // In demo mode, save user data to localStorage
        if (isDemoModeEnabled && newToken.startsWith("demo_")) {
          localStorage.setItem("demoUser", JSON.stringify(userData));
        }

        queryClient.setQueryData(["profile"], userData);
        toast.success("Login successful");
      }
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Login failed");
    },
  });

  // OTP verification mutation
  const verifyOtpMutation = useMutation({
    mutationFn: ({ sessionId, otp }) => {
      if (isDemoModeEnabled) {
        // In demo mode, accept any OTP
        return new Promise((resolve, reject) => {
          setTimeout(() => {
            if (otp && otp.length >= 4) {
              resolve({
                data: {
                  token: `demo_token_${otpSession.user.role}`,
                  user: {
                    ...otpSession.user,
                    id: Date.now(),
                    email: String(otpSession.user.email || "demo@user.com"),
                    personalInfo:
                      otpSession.user.role === "investor"
                        ? {
                            firstName: "Demo",
                            lastName: "User",
                          }
                        : null,
                    corporateInfo:
                      otpSession.user.role !== "investor"
                        ? {
                            companyName: "Demo Company",
                          }
                        : null,
                  },
                },
              });
            } else {
              reject(new Error("Invalid OTP"));
            }
          }, 1000);
        });
      } else {
        // Real API mode - call the actual login OTP verification endpoint
        return api.post("/api/auth/verify-login-otp", { sessionId, otp });
      }
    },
    onSuccess: (response) => {
      const { token: newToken, user: userData } = response.data;
      setToken(newToken);
      localStorage.setItem("token", newToken);

      // In demo mode, save user data to localStorage
      if (isDemoModeEnabled && newToken.startsWith("demo_")) {
        localStorage.setItem("demoUser", JSON.stringify(userData));
      }

      queryClient.setQueryData(["profile"], userData);
      setOtpSession(null); // Clear OTP session
      toast.success("Login successful");
    },
    onError: (error) => {
      toast.error(error.message || "OTP verification failed");
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: (userData) => {
      // Check if it's FormData (KYC/KYB) or regular data
      if (userData instanceof FormData) {
        return api.post("/api/auth/register-kyc-kyb", userData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      } else {
        return api.post("/api/auth/register", userData);
      }
    },
    onSuccess: (response) => {
      const data = response.data;
      const userData = data.user || data.data?.user;
      const newToken = data.token || data.data?.token;

      // Only auto-login if user is active
      if (userData && userData.isActive) {
        setToken(newToken);
        localStorage.setItem("token", newToken);
        queryClient.setQueryData(["profile"], userData);
        toast.success(data.message || "Registration successful");
      } else {
        // User registered but is inactive - don't auto-login, redirect to login
        toast.success(
          data.message ||
            "Registration submitted successfully. Please wait for admin approval"
        );

        // Redirect to login page after a short delay to let user read the message
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      }
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.message || "Registration failed";
      toast.error(errorMessage);
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: (profileData) => api.put("/api/auth/profile", profileData),
    onSuccess: (response) => {
      const data = response.data;
      const userData = data.user || data.data?.user;
      queryClient.setQueryData(["profile"], userData);
      toast.success(data.message || "Profile updated successfully");
    },
    onError: (error) => {
      const errorMessage =
        error.response?.data?.message || "Failed to update profile";
      toast.error(errorMessage);
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: (passwordData) =>
      api.put("/api/auth/change-password", passwordData),
    onSuccess: async () => {
      toast.success("Password changed successfully");
      // Refetch user profile to update mustChangePassword flag
      await queryClient.invalidateQueries(["profile"]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to change password");
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: () => api.post("/api/auth/logout"),
    onSuccess: () => {
      // Clear local state after successful API call
      setToken(null);
      localStorage.removeItem("token");
      localStorage.removeItem("demoUser"); // Clear demo user data
      queryClient.clear();
      delete api.defaults.headers.common["Authorization"];
      toast.success("Logged out successfully");
    },
    onError: (error) => {
      // Even if API call fails, still clear local state
      console.warn("Logout API failed, clearing local state anyway:", error);
      setToken(null);
      localStorage.removeItem("token");
      localStorage.removeItem("demoUser"); // Clear demo user data
      queryClient.clear();
      delete api.defaults.headers.common["Authorization"];
      toast.success("Logged out");
    },
  });

  // Login function (first step)
  const login = async (credentials) => {
    return loginMutation.mutateAsync(credentials);
  };

  // OTP verification function
  const verifyOtp = async (otp) => {
    if (!otpSession) {
      throw new Error("No OTP session found");
    }
    return verifyOtpMutation.mutateAsync({
      sessionId: otpSession.sessionId,
      otp,
    });
  };

  // Resend OTP function
  const resendOtp = async () => {
    if (!otpSession) {
      throw new Error("No OTP session found");
    }

    if (isDemoModeEnabled) {
      // Demo mode - simulate resend
      return new Promise((resolve) => {
        setTimeout(() => {
          toast.success("OTP resent successfully");
          resolve();
        }, 1000);
      });
    } else {
      // Real API mode - call resend endpoint
      try {
        const response = await api.post("/api/auth/resend-login-otp", {
          sessionId: otpSession.sessionId,
        });
        toast.success("OTP resent successfully");
        return response.data;
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to resend OTP");
        throw error;
      }
    }
  };

  // Clear OTP session
  const clearOtpSession = () => {
    setOtpSession(null);
  };

  // Register function
  const register = async (userData) => {
    return registerMutation.mutateAsync(userData);
  };

  // Update profile function
  const updateProfile = async (profileData) => {
    return updateProfileMutation.mutateAsync(profileData);
  };

  // Change password function
  const changePassword = async (passwordData) => {
    return changePasswordMutation.mutateAsync(passwordData);
  };

  // Improved logout function
  const logout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      // Function already handles error in mutation
    }
  };

  // Quick logout without API call (for emergencies or when offline)
  const quickLogout = () => {
    setToken(null);
    localStorage.removeItem("token");
    localStorage.removeItem("demoUser"); // Clear demo user data
    queryClient.clear();
    delete api.defaults.headers.common["Authorization"];
    toast.success("Logged out quickly");
  };

  // Check if user is authenticated
  const isAuthenticated = !!token && !!user;

  // Check if user has specific role
  const hasRole = (role) => {
    return user?.role === role;
  };

  // Check if user has any of the specified roles
  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  // Get user display name
  const getDisplayName = () => {
    if (!user) {
      return "User";
    }

    try {
      if (user?.role === "investor" && user?.personalInfo) {
        const firstName = String(user.personalInfo.firstName || "");
        const lastName = String(user.personalInfo.lastName || "");
        const fullName = `${firstName} ${lastName}`.trim();
        const result = fullName || String(user.email || "User");
        return result;
      }

      if (user?.corporateInfo?.companyName) {
        const result = String(user.corporateInfo.companyName);
        return result;
      }

      if (user?.email) {
        const result = String(user.email);
        return result;
      }

      return "User";
    } catch (error) {
      console.error("Error in getDisplayName:", error);
      return "User";
    }
  };

  const value = {
    // User state
    user,
    token,
    loading: actualLoading,
    isAuthenticated,

    // OTP state
    otpSession,

    // Auth functions
    login,
    verifyOtp,
    resendOtp,
    clearOtpSession,
    register,
    logout,
    quickLogout,
    updateProfile,
    changePassword,

    // Utility functions
    hasRole,
    hasAnyRole,
    getDisplayName,

    // Loading states
    isLoggingIn: loginMutation.isPending,
    isVerifyingOtp: verifyOtpMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isUpdatingProfile: updateProfileMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
